import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { Add as AddIcon } from "@material-ui/icons";
import { Box, Grid } from "@material-ui/core";
import translate from "i18n/translate";
import View from "components/utils/View";
import BankAccountsCard from "./BankAccountsCard";
import BankAccountWithdrawDialog from "./BankAccountWithdrawDialog";
import CreateBankAccountDialog from "./CreateBankAccountDialog";
import useBankAccount from "./useBankAccount";
import useStyles from "./bankAccountsStyles";
import { SrButton, SrContentHeader, SrContentWrapper } from "../../../common";

const BankAccounts = () => {
  const classes = useStyles();
  const { user } = useSelector(({ auth }) => auth);
  const funds = useSelector(({ app }) => app.funds);
  const {
    addBankAccount,
    setDefaultBankAccount,
    bankAccounts,
    loading: bankAccountLoading,
  } = useBankAccount();

  const [initLoading, setInitLoading] = useState(true);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openWithdrawDialog, setOpenWithdrawDialog] = useState(false);

  useEffect(() => {
    if (!bankAccountLoading) {
      setInitLoading(false);
    }
  }, [bankAccountLoading]);

  const showInitialLoading = bankAccountLoading && initLoading;

  const sortingCriteria = (bankAccountA) => (!bankAccountA.default ? 1 : -1);

  const showWithdrawButton =
    !!bankAccounts.length &&
    !user.automatic_withdrawal &&
    funds.available_funds > 0;

  return (
    <View translateTitleKey="menu.bankAccounts">
      <Grid container direction="column" spacing={2}>
        <SrContentHeader
          title={translate("bankAccounts.heading")}
          actions={
            <div className={classes.actions}>
              {showWithdrawButton && (
                <SrButton
                  color="primary"
                  variant="contained"
                  onClick={() => setOpenWithdrawDialog(true)}
                >
                  {translate("bankAccounts.withdraw")}
                </SrButton>
              )}
              <SrButton
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
              >
                {translate("bankAccounts.addAccount")}
              </SrButton>
            </div>
          }
        />
        <SrContentWrapper
          isLoading={showInitialLoading}
          isEmpty={!bankAccounts.length}
          emptyMessage={translate("bankAccounts.emptyList")}
        >
          <Box
            maxWidth={720}
            width="100%"
            display="flex"
            flexDirection="column"
            margin="auto"
            marginTop={2}
          >
            <AnimatePresence>
              {bankAccounts.sort(sortingCriteria).map(
                ({
                  id,
                  account_number_suffix: accountNo,
                  alias,
                  active,
                  authorized,
                  activation_status: status,
                  bank_name: bankName,
                  default: isDefault,
                  origin,
                }) =>
                  status !== "deleted" && (
                    <motion.div key={id} layout>
                      <BankAccountsCard
                        id={id}
                        accountNo={accountNo}
                        accountHolder={alias}
                        status={status || (active ? "active" : "pending")}
                        bankName={bankName}
                        isDefault={isDefault}
                        handleSetAsDefault={setDefaultBankAccount}
                        origin={origin}
                        isDefaultable={authorized === "S"}
                      />
                    </motion.div>
                  )
              )}
            </AnimatePresence>
          </Box>
          <BankAccountWithdrawDialog
            open={openWithdrawDialog}
            bankAccounts={bankAccounts}
            handleClose={() => setOpenWithdrawDialog(false)}
          />
        </SrContentWrapper>

        <CreateBankAccountDialog
          open={openCreateDialog}
          handleClose={() => setOpenCreateDialog(false)}
          handleSubmit={addBankAccount}
        />
      </Grid>
    </View>
  );
};

export default BankAccounts;
